import React, { createContext, useContext, useState, useEffect } from 'react';
import GlobalWarning from './GlobalWarning';
import { getActiveWarning } from './warningConfig.js';

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [currentAlert, setCurrentAlert] = useState(null);

  // Check for active warnings on mount and daily
  useEffect(() => {
    const checkForActiveWarning = () => {
      const activeWarning = getActiveWarning();
      setCurrentAlert(activeWarning);
    };

    // Check immediately
    checkForActiveWarning();

    // Check daily at midnight
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    const midnightTimeout = setTimeout(() => {
      checkForActiveWarning();
      // Set up daily interval
      const dailyInterval = setInterval(checkForActiveWarning, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(midnightTimeout);
  }, []);

  // Centralized localStorage utilities
  const isAlertDismissed = storageKey => {
    return localStorage.getItem(storageKey) === 'true';
  };

  const setAlertDismissed = storageKey => {
    localStorage.setItem(storageKey, 'true');
  };

  const clearDismissalStatus = storageKey => {
    localStorage.removeItem(storageKey);
  };

  const showAlert = alertConfig => {
    setCurrentAlert(alertConfig);
  };

  const hideAlert = () => {
    setCurrentAlert(null);
  };

  const contextValue = {
    currentAlert,
    showAlert,
    hideAlert,
    isAlertDismissed,
    setAlertDismissed,
    clearDismissalStatus
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <GlobalWarning
        message={currentAlert?.message}
        severity={currentAlert?.severity}
        dismissible={currentAlert?.dismissible}
        storageKey={currentAlert?.storageKey}
      />
    </AlertContext.Provider>
  );
};

export default AlertProvider;
